import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ChevronLeft, CheckCircle, Mail, Phone, User, Building, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const ReviewSubmitStep = ({ formData, onBack, onSubmit }) => {
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!consent) {
      toast.error('Please confirm you have permission to share these referrals');
      return;
    }

    setSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/referrals/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${formData.verificationToken}`
        },
        body: JSON.stringify({
          referrer: {
            first_name: formData.referrerFirstName,
            last_name: formData.referrerLastName,
            email: formData.referrerEmail,
            phone: formData.referrerPhone
          },
          referrals: (formData.referrals || []).map(ref => ({
            first_name: ref.firstName,
            last_name: ref.lastName,
            email: ref.email,
            phone: ref.phone || null,
            lead_type: ref.type,
            services_interested: ref.services || [],
            personal_message: ref.message || null,
            send_email: ref.sendEmail
          }))
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Referrals submitted successfully!');
        onSubmit(data);
      } else {
        toast.error(data.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0B2240] mb-2">
          Review Your Referral(s)
        </h2>
        <p className="text-[#4C5A70]">
          Please review all details before submitting
        </p>
      </div>

      {/* Referrer Info */}
      <Card className="p-4 bg-white border-2 border-[#E1DFD7]">
        <div className="flex gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <p className="font-medium text-[#0B2240] mb-1">You (Referrer)</p>
            <p className="text-sm text-[#4C5A70]">{formData.referrerFirstName} {formData.referrerLastName}</p>
            <p className="text-sm text-[#4C5A70]">{formData.referrerEmail} ✓ Verified</p>
            <p className="text-sm text-[#4C5A70]">{formData.referrerPhone}</p>
          </div>
        </div>
      </Card>

      {/* Referrals */}
      <div className="space-y-3">
        {(formData.referrals || []).map((ref, index) => (
          <Card key={index} className="p-4 bg-white border-2 border-[#E1DFD7]">
            <div className="flex justify-between items-start">
              <div className="flex gap-3 flex-1">
                {ref.type === 'business' ? (
                  <Building className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                ) : (
                  <User className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-[#0B2240] mb-1">Referral {index + 1}: {ref.firstName} {ref.lastName}</p>
                  <div className="space-y-1 text-sm text-[#4C5A70]">
                    <p className="flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {ref.email}
                    </p>
                    {ref.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        {ref.phone}
                      </p>
                    )}
                    <p>Type: {ref.type === 'business' ? 'Business Owner' : 'Individual/Family'}</p>
                    {ref.services?.length > 0 && (
                      <p>Services: {ref.services.length} selected</p>
                    )}
                    {ref.message && (
                      <div className="mt-2 p-2 bg-[#F7F6F2] rounded">
                        <p className="text-xs"><strong>Your note:</strong> {ref.message}</p>
                      </div>
                    )}
                    <p className="mt-2">
                      {ref.sendEmail ? (
                        <span className="text-green-600 font-medium">✓ Will send introduction email</span>
                      ) : (
                        <span className="text-[#4C5A70]">Manual follow-up by KGOB</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Consent */}
      <div className="bg-[#F7F6F2] p-6 rounded-lg border-2 border-[#E1DFD7]">
        <div className="flex items-start gap-3">
          <Checkbox data-testid="review-submit-step-checkbox-1"
            id="consent"
            checked={consent}
            onCheckedChange={setConsent}
          />
          <Label htmlFor="consent" className="text-sm text-[#4C5A70] cursor-pointer">
            I confirm these individuals would welcome an introduction from KGOB, or I have their permission to share their information.
          </Label>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button data-testid="review-submit-step-button-2"
          onClick={onBack}
          variant="outline"
          className="h-12 px-6 border-2 border-[#0B2240] text-[#0B2240]"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button data-testid="review-submit-step-button-1"
          onClick={handleSubmit}
          disabled={!consent || submitting}
          className="flex-1 h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
        >
          {submitting ? 'Submitting...' : `Submit ${formData.referrals.length} Referral${formData.referrals.length > 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
};